import React, { Component } from "react";
import CartContext from "./CartContext";
import { SideMenu } from "./SideMenu";
import { RawMaterialCategory } from "./RawMaterialCategory";
import {
  BiSave,
  BiRefresh,
  BiUpload,
  BiDownload,
  BiBarChart,
} from "react-icons/bi";

import { BsFillPencilFill } from "react-icons/bs";
import { MessageBox } from "./MessageBox";
import { LoadSpinner } from "./LoadSpinner";
import axios from "axios";
import Fuse from "fuse.js";
import Swal from "sweetalert2";
import RmInventoryDashboard from "./RmInventoryDashboard";

import {
  OverlayTrigger,
  FloatingLabel,
  Tooltip,
  Stack,
  FormControl,
  Button,
  ButtonToolbar,
  Form,
  Row,
  Col,
  Modal,
  InputGroup,
  Nav,
  Table,
  Navbar,
  Pagination,
  Badge,
} from "react-bootstrap";

// Utility: debounce (simple, no external deps)
function debounce(fn, ms = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

const HOSTNAME_STEEL = "sharoffsteel-admin.elasticcart.com"; // centralize hostname check

export class RawMaterialsInventory extends Component {
  static contextType = CartContext;
  constructor(props) {
    super(props);
    this.state = {
      rawmaterialsinventory: [],
      rawmaterialsinventoryFiltered: [],
      rawmaterialcategory: [],
      rawmaterialwarehouse: [],

      // UI state
      msgboxOpen: false,
      msgboxTitle: "",
      msgboxMessage: "",
      loadSpinnerOpen: false,
      addModalShow: false,

      // filters + search
      categoryFilterName: localStorage.getItem("rmi.category") || "",
      warehouseFilterName: localStorage.getItem("rmi.warehouse") || "",
      searchquery: "",

      // pagination
      page: 1,
      pageSize: 25,

      // sorting
      sortBy: "name", // default sort
      sortDir: "asc", // or 'desc'
      showAnalytics: false, // ✅ new state
    };
    this.inputFile = React.createRef();

    // bind
    this.onSearchChangeDebounced = debounce(this.onSearchChangeImmediate, 300);
  }

  // ------- Fetching -------
  async GetRawMaterialCategories() {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API}Rawmaterials/GetRawMaterialCategories/${this.context.storeId}`
      );
      const data = await response.json();
      if (data?.status !== 400) {
        this.setState({ rawmaterialcategory: data });
      }
    } catch (error) {
      console.error("GetRawMaterialCategories error", error);
    }
  }

  async GetRawMaterialWarehouses() {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API}Warehouse/GetRawMaterialWarehouses/${this.context.storeId}`
      );
      const data = await response.json();
      if (data?.status !== 400) {
        this.setState({ rawmaterialwarehouse: data });
      }
    } catch (error) {
      console.error("GetRawMaterialWarehouses error", error);
    }
  }

  async GetRawMaterialsInventories() {
    try {
      this.setState({ rawmaterialsinventoryFiltered: [] });
      const response = await fetch(
        `${process.env.REACT_APP_API}Rawmaterials/GetRawMaterialsInventories/${this.context.storeId}`
      );
      const data = await response.json();
      if (data?.status !== 400) {
        this.setState(
          {
            rawmaterialsinventory: data,
          },
          () => this.applyFilters()
        );
      }
    } catch (error) {
      console.error("GetRawMaterialsInventories error", error);
    }
  }

  // ------- Helpers -------
  ShowMessagBox = (title, message) => {
    this.setState({
      msgboxOpen: true,
      msgboxTitle: title,
      msgboxMessage: message,
    });
  };
  CloseMessagBoxCallback = () => this.setState({ msgboxOpen: false });
  ShowLoadSpinner = () => this.setState({ loadSpinnerOpen: true });
  HideLoadSpinner = () => this.setState({ loadSpinnerOpen: false });

  OnSaved = () => this.OnRefreshButtonClick();
  OnDeleted = () => this.OnRefreshButtonClick();

  OnRefreshButtonClick = () => {
    this.GetRawMaterialsInventories();
  };

  // ------- Search & Filters -------
  onSearchChange = (query) => {
    this.setState({ searchquery: query, page: 1 }, () =>
      this.onSearchChangeDebounced(query)
    );
  };

  onSearchChangeImmediate = () => {
    this.applyFilters();
  };

  OnCategoryChanged = (e) => {
    const val = e.target.value;
    localStorage.setItem("rmi.category", val);
    this.setState({ categoryFilterName: val, page: 1 }, () =>
      this.applyFilters()
    );
  };
  OnWarehouseChanged = (e) => {
    const val = e.target.value;
    localStorage.setItem("rmi.warehouse", val);
    this.setState({ warehouseFilterName: val, page: 1 }, () =>
      this.applyFilters()
    );
  };

  onSort = (col) => {
    this.setState((s) => {
      const dir =
        s.sortBy === col ? (s.sortDir === "asc" ? "desc" : "asc") : "asc";
      return { sortBy: col, sortDir: dir, page: 1 };
    }, this.applyFilters);
  };

  sortIndicator = (col) => {
    const { sortBy, sortDir } = this.state;
    if (sortBy !== col) return null;
    return <span className="ms-1">{sortDir === "asc" ? "▲" : "▼"}</span>;
  };

  applyFilters = () => {
    const {
      rawmaterialsinventory,
      searchquery,
      categoryFilterName,
      warehouseFilterName,
      sortBy,
      sortDir,
    } = this.state;

    let list = [...rawmaterialsinventory];

    // text search with numeric tolerance for thickness
    if (searchquery && searchquery.trim().length > 0) {
      const q = searchquery.trim();

      // 1) Fuzzy search across multiple fields (incl. thickness as string)
      const fuse = new Fuse(list, {
        keys: [
          "thickness",
          "internalId",
          "name",
          "remarks",
          "hsnCode",
          "batchId",
          "qualityId",
        ],
        threshold: 0.3, // partial matches
        ignoreLocation: true,
        distance: 200,
      });
      const matchSet = new Set(fuse.search(q).map((r) => r.item));

      // 2) Numeric proximity search for thickness (e.g., "6" ≈ 5.97–6.05)
      const numQ = Number(q);
      if (!Number.isNaN(numQ)) {
        const EPS = 0.05; // adjust band if you like
        list.forEach((item) => {
          const t = Number(item?.thickness);
          if (!Number.isNaN(t) && Math.abs(t - numQ) <= EPS) matchSet.add(item);
          // also match strings like "6mm"
          else if (
            String(item?.thickness ?? "")
              .toLowerCase()
              .includes(q.toLowerCase())
          )
            matchSet.add(item);
        });
      }

      list = Array.from(matchSet);
    }

    // dropdown filters
    list = list.filter((f) => {
      const matchCat =
        !categoryFilterName ||
        String(f.categoryId) === String(categoryFilterName);
      const matchWh =
        !warehouseFilterName ||
        String(f.warehouseId) === String(warehouseFilterName);
      return matchCat && matchWh;
    });

    // ✅ sort final list by name (or by sortBy)
    list.sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      if (sortBy === "name") {
        return String(a.name).localeCompare(String(b.name)) * dir;
      }
      // fallback: generic
      return (
        String(a[sortBy] ?? "").localeCompare(String(b[sortBy] ?? "")) * dir
      );
    });

    this.setState({ rawmaterialsinventoryFiltered: list });
  };

  // CSV upload/download
  onUploadButtonClick = () => this.inputFile.current?.click();

  handleFileUpload = (e) => {
    const { files } = e.target;
    if (!files || !files[0]) return;

    this.ShowLoadSpinner();
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result;
      if (!text) {
        this.HideLoadSpinner();
        this.ShowMessagBox("Error", "Empty file uploaded.");
        return;
      }
      this.UploadProductsFromCSV(text);
    };
    reader.readAsText(files[0]);
  };

  UploadProductsFromCSV = (csvstr) => {
    this.ShowLoadSpinner();

    fetch(
      `${process.env.REACT_APP_API}Rawmaterials/UploadRawMaterialsFromCsv/${this.context.storeId}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" }, // 👈 JSON
        body: JSON.stringify(csvstr), // 👈 JSON string
      }
    )
      .then((response) => {
        if (!response.ok) throw new Error("Upload failed");
        return response.text();
      })
      .then((data) => {
        this.HideLoadSpinner();
        this.OnRefreshButtonClick();
        this.ShowMessagBox(
          "Information",
          `Uploaded Successfully. Please Refresh (F5) the Portal!\n\n${data}`
        );
      })
      .catch((error) => {
        this.HideLoadSpinner();
        this.ShowMessagBox("Error", "Failed - Invalid CSV file format");
        console.error("UploadProductsFromCSV error", error);
      });
  };

  DownloadProductsToCSV = () => {
    this.ShowLoadSpinner();

    const rows = this.state.rawmaterialsinventory;
    // if (!rows.length) {
    //   this.ShowMessagBox("Info", "No data to export.");
    //   this.HideLoadSpinner();
    //   return;
    // }

    // build lookup maps
    const warehouseMap = Object.fromEntries(
      this.state.rawmaterialwarehouse.map((w) => [w.id, w.name])
    );
    const categoryMap = Object.fromEntries(
      this.state.rawmaterialcategory.map((c) => [c.id, c.name])
    );

    // column mapping (added Total at the end)
    const headers = {
      warehouseId: "Warehouse",
      categoryId: "CategoryName",
      name: "Name",
      thickness: "Thickness",
      width: "Width",
      remarks: "Remarks",
      internalId: "InternalId",
      batchId: "BatchID",
      hsnCode: "HSNCode",
      qualityId: "QualityID",
      pieceCount: "Pieces",
      localWt: "LocalWt",
      instock: "Instock Qty",
      unit: "Unit",
      purchasePrice: "PurchasePrice",
      total: "Total", // new column
    };

    const csvContent = [
      Object.values(headers).join(","), // header row
      ...rows.map((r) =>
        Object.keys(headers)
          .map((h) => {
            if (h === "warehouseId") return warehouseMap[r[h]] || r[h] || "";
            if (h === "categoryId") return categoryMap[r[h]] || r[h] || "";
            if (h === "total") {
              const price = Number(r?.purchasePrice ?? 0);
              const qty = Number(r?.instock ?? 0);
              return (price * qty).toFixed(2); // formatted total
            }
            return (r?.[h] ?? "").toString();
          })
          .map((c) => `"${c.replace(/"/g, '""')}"`)
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    const now = new Date();
    const fileName = `rawmaterial-${now.getFullYear()}-${String(
      now.getMonth() + 1
    ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}-${String(
      now.getHours()
    ).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}.csv`;

    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    this.HideLoadSpinner();
  };

  DownloadFilteredCSV = () => {
    const rows = this.state.rawmaterialsinventoryFiltered;
    if (!rows.length) {
      this.ShowMessagBox("Info", "No data to export.");
      this.HideLoadSpinner();
      return;
    }

    // build lookup maps
    const warehouseMap = Object.fromEntries(
      this.state.rawmaterialwarehouse.map((w) => [w.id, w.name])
    );
    const categoryMap = Object.fromEntries(
      this.state.rawmaterialcategory.map((c) => [c.id, c.name])
    );

    // column mapping (added Total at the end)
    const headers = {
      warehouseId: "Warehouse",
      categoryId: "CategoryName",
      name: "Name",
      remarks: "Remarks",
      internalId: "InternalId",
      batchId: "BatchID",
      hsnCode: "HSNCode",
      qualityId: "QualityID",
      pieceCount: "Pieces",
      localWt: "LocalWt",
      instock: "Instock Qty",
      unit: "Unit",
      purchasePrice: "PurchasePrice",
      total: "Total", // new column
    };

    const csvContent = [
      Object.values(headers).join(","), // header row
      ...rows.map((r) =>
        Object.keys(headers)
          .map((h) => {
            if (h === "warehouseId") return warehouseMap[r[h]] || r[h] || "";
            if (h === "categoryId") return categoryMap[r[h]] || r[h] || "";
            if (h === "total") {
              const price = Number(r?.purchasePrice ?? 0);
              const qty = Number(r?.instock ?? 0);
              return (price * qty).toFixed(2); // formatted total
            }
            return (r?.[h] ?? "").toString();
          })
          .map((c) => `"${c.replace(/"/g, '""')}"`)
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    const now = new Date();
    const fileName = `rawmaterial-${now.getFullYear()}-${String(
      now.getMonth() + 1
    ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}-${String(
      now.getHours()
    ).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}.csv`;

    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    this.HideLoadSpinner();
  };

  // pagination helpers
  setPage = (p) => this.setState({ page: p });
  pagedItems = () => {
    const { rawmaterialsinventoryFiltered, page, pageSize } = this.state;
    const start = (page - 1) * pageSize;
    return rawmaterialsinventoryFiltered.slice(start, start + pageSize);
  };

  // totals
  totals = () => {
    const rows = this.pagedItems(); // show totals for current page to match table footer
    const sum = (arr, key) =>
      arr.reduce((s, r) => s + (parseFloat(r?.[key]) || 0), 0);
    return {
      pieceCount: sum(rows, "pieceCount"),
      localWt: sum(rows, "localWt"),
      instock: sum(rows, "instock"),
      value: rows.reduce(
        (s, r) =>
          s + parseFloat(r.purchasePrice || 0) * parseFloat(r.instock || 0),
        0
      ),
    };
  };

  componentDidMount = () => {
    window.scrollTo(0, 0);
    if (!this.state.firstLoad) {
      this.setState({ firstLoad: true });
      this.GetRawMaterialsInventories();
      this.GetRawMaterialCategories();
      this.GetRawMaterialWarehouses();
    }
    this.keydownHandler = (event) => {
      if (event.key === "F8") this.setState({ addModalShow: true });
      else if (event.key === "F9") this.OnRefreshButtonClick();
    };
    document.addEventListener("keydown", this.keydownHandler);
  };
  componentWillUnmount() {
    document.removeEventListener("keydown", this.keydownHandler);
  }

  totalValueINR = () => {
    const { rawmaterialsinventoryFiltered } = this.state;
    const total = rawmaterialsinventoryFiltered.reduce((sum, r) => {
      const price = parseFloat(r.purchasePrice || 0);
      const qty = parseFloat(r.instock || 0);
      return sum + price * qty;
    }, 0);
    const locale =
      this.context.storeSettings === ""
        ? "en-IN"
        : this.context.storeSettings.defaultLocale;
    const currency =
      this.context.storeSettings === ""
        ? "INR"
        : this.context.storeSettings.defaultCurrency;
    try {
      return total.toLocaleString(locale, {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
      });
    } catch {
      return total.toLocaleString("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      });
    }
  };

  render() {
    const {
      loadSpinnerOpen,
      msgboxOpen,
      msgboxTitle,
      msgboxMessage,
      addModalShow,
    } = this.state;
    const totalPages = Math.max(
      1,
      Math.ceil(
        this.state.rawmaterialsinventoryFiltered.length / this.state.pageSize
      )
    );
    const t = this.totals();

    const locale =
      this.context.storeSettings === ""
        ? "en-IN"
        : this.context.storeSettings.defaultLocale;
    const currency =
      this.context.storeSettings === ""
        ? "INR"
        : this.context.storeSettings.defaultCurrency;

    const fmtMoney = (n) => {
      try {
        return n.toLocaleString(locale, {
          style: "currency",
          currency,
          maximumFractionDigits: 0,
        });
      } catch {
        return n.toLocaleString("en-IN", {
          style: "currency",
          currency: "INR",
          maximumFractionDigits: 0,
        });
      }
    };

    return (
      <>
        {loadSpinnerOpen ? <LoadSpinner open="true" /> : null}
        <div className="Container-fluid p-0 ">
          <input
            style={{ display: "none" }}
            accept=".csv"
            ref={this.inputFile}
            onChange={this.handleFileUpload}
            type="file"
            multiple="single"
          />
          <Row>
            <div
              className="fixedTableHightOrder"
              style={{ height: window.innerHeight - 90 + "px" }}
            >
              {msgboxOpen ? (
                <MessageBox
                  open={msgboxOpen}
                  title={msgboxTitle}
                  message={msgboxMessage}
                  callback={this.CloseMessagBoxCallback}
                />
              ) : null}
              <Navbar className="shadow position-sticky fixed-top py-3 text-black ">
                <h4 className="mb-0">
                  <b>Raw Materials Inventory</b> &nbsp;
                  {/* <Badge
                    bg="light"
                    text="dark"
                    title="F8 opens Add, F9 refreshes"
                  >
                    Shortcuts: F8 / F9
                  </Badge> */}
                </h4>
                <Navbar.Collapse className="justify-content-end mx-0 ">
                  <Nav>
                    {window.innerWidth > 900 ? (
                      <>
                        <ButtonToolbar className="text-end align-middle">
                          <Button
                            size="sm"
                            className="mainthemecolor"
                            onClick={() =>
                              this.setState({ addModalShow: true })
                            }
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-plus"
                              viewBox="0 0 16 16"
                            >
                              <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
                            </svg>
                            &nbsp; Add (F8)
                          </Button>
                          &nbsp;
                          <Button
                            className="mainthemecolor"
                            size="sm"
                            onClick={this.OnRefreshButtonClick}
                          >
                            <BiRefresh size="16" />
                            &nbsp;Refresh (F9)
                          </Button>
                          &nbsp;
                          {/* ✅ Analytics Button */}
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() =>
                              this.setState({ showAnalytics: true })
                            }
                          >
                            <BiBarChart size={16} /> &nbsp;Analytics
                          </Button>
                          &nbsp;
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            // disabled={
                            //   this.context.UserRoles.PRODUCTADMIN !==
                            //   this.context.userData.roleId
                            // }
                            onClick={this.onUploadButtonClick}
                          >
                            <BiUpload size="16" />
                            &nbsp;Upload from CSV
                          </Button>
                          &nbsp;
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={this.DownloadProductsToCSV}
                          >
                            <BiDownload size="16" />
                            &nbsp;Download as CSV
                          </Button>
                          &nbsp;
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={this.DownloadFilteredCSV}
                          >
                            <BiDownload size={16} />
                            &nbsp;Download Filtered
                          </Button>
                          {addModalShow ? (
                            <RawMaterialsInventoryAddOrEditModal
                              show={addModalShow}
                              Type="Add"
                              onHide={() =>
                                this.setState({ addModalShow: false }, () =>
                                  this.OnRefreshButtonClick()
                                )
                              }
                              rawmaterialcategory={
                                this.state.rawmaterialcategory
                              }
                              rawmaterialwarehouse={
                                this.state.rawmaterialwarehouse
                              }
                            />
                          ) : null}
                        </ButtonToolbar>
                      </>
                    ) : (
                      <>
                        <ButtonToolbar className="text-end align-middle">
                          <Button
                            size="sm"
                            className="mainthemecolor"
                            onClick={() =>
                              this.setState({ addModalShow: true })
                            }
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-plus"
                              viewBox="0 0 16 16"
                            >
                              <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
                            </svg>
                            &nbsp; Add
                          </Button>
                          &nbsp;
                          <Button
                            className="mainthemecolor"
                            size="sm"
                            onClick={this.OnRefreshButtonClick}
                          >
                            <BiRefresh size="16" />
                          </Button>
                          &nbsp;
                          {/* ✅ Analytics Button */}
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() =>
                              this.setState({ showAnalytics: true })
                            }
                          >
                            <BiBarChart size={16} />
                          </Button>
                          &nbsp;
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            // disabled={
                            //   this.context.UserRoles.PRODUCTADMIN !==
                            //   this.context.userData.roleId
                            // }
                            onClick={this.onUploadButtonClick}
                          >
                            <BiUpload size="16" />
                            &nbsp;UP CSV
                          </Button>
                          &nbsp;
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={this.DownloadProductsToCSV}
                          >
                            <BiDownload size="16" />
                            &nbsp;DN CSV
                          </Button>
                          {addModalShow ? (
                            <RawMaterialsInventoryAddOrEditModal
                              show={addModalShow}
                              Type="Add"
                              onHide={() =>
                                this.setState({ addModalShow: false }, () =>
                                  this.OnRefreshButtonClick()
                                )
                              }
                              rawmaterialcategory={
                                this.state.rawmaterialcategory
                              }
                              rawmaterialwarehouse={
                                this.state.rawmaterialwarehouse
                              }
                            />
                          ) : null}
                        </ButtonToolbar>
                      </>
                    )}
                  </Nav>
                </Navbar.Collapse>
              </Navbar>

              <div className="purposestyle">
                <h5>
                  <b>Purpose:</b> Manage all Rawmaterials Inventory and Stocks.
                  Rawmaterials are used in Product Production and Product
                  Servicing Modules.
                </h5>
              </div>

              <div className="p-3">
                <Row className="g-2">
                  <Col md={6}>
                    <Stack direction="horizontal" gap={3}>
                      <b>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi bi-search"
                          viewBox="0 0 16 16"
                        >
                          <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
                        </svg>
                      </b>
                      <FormControl
                        type="search"
                        placeholder="Search name, thickness, HSN, internal/batch/quality"
                        className="text-primary fw-bold bg-white "
                        aria-label="Search"
                        onChange={(e) => this.onSearchChange(e.target.value)}
                      />
                    </Stack>
                  </Col>
                  <Col md={3}>
                    <InputGroup>
                      <InputGroup.Text id="basic-addon1">
                        Category
                      </InputGroup.Text>
                      <Form.Select
                        value={this.state.categoryFilterName}
                        onChange={this.OnCategoryChanged}
                      >
                        <option value="">--All Category--</option>
                        {this.state.rawmaterialcategory.map((m) => (
                          <option value={m.id} key={m.id}>
                            {m.name}
                          </option>
                        ))}
                      </Form.Select>
                    </InputGroup>
                  </Col>
                  <Col md={3}>
                    <InputGroup>
                      <InputGroup.Text id="basic-addon2">
                        Warehouse
                      </InputGroup.Text>
                      <Form.Select
                        value={this.state.warehouseFilterName}
                        onChange={this.OnWarehouseChanged}
                      >
                        <option value="">
                          <option value="">--All Warehouse--</option>
                        </option>
                        {this.state.rawmaterialwarehouse.map((m) => (
                          <option value={m.id} key={m.id}>
                            {m.name}
                          </option>
                        ))}
                      </Form.Select>
                    </InputGroup>
                  </Col>
                </Row>
              </div>

              <div className="px-3 pb-2 d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-muted">
                    Showing <b>{this.pagedItems().length}</b> of{" "}
                    <b>{this.state.rawmaterialsinventoryFiltered.length}</b>{" "}
                    items
                  </small>
                </div>
                <div>
                  <small className="text-muted">
                    Total value (filtered): <b>{this.totalValueINR()}</b>
                  </small>
                </div>
              </div>

              <div>
                <Table
                  border
                  striped
                  size="sm"
                  className="px-0 bg-white "
                  style={{ fontSize: "12px" }}
                >
                  <thead>
                    {window.innerWidth > 900 ? (
                      <>
                        <tr className="text-center text-black bg-white border">
                          <th>#</th>
                          <th
                            width="15%"
                            className="text-start"
                            role="button"
                            onClick={() => this.onSort("name")}
                          >
                            RM Name {this.sortIndicator("name")}
                          </th>
                          <th
                            width="5%"
                            className="text-start"
                            role="button"
                            onClick={() => this.onSort("thickness")}
                          >
                            Thick {this.sortIndicator("thickness")}
                          </th>
                          <th
                            width="5%"
                            className="text-start"
                            role="button"
                            onClick={() => this.onSort("width")}
                          >
                            Width {this.sortIndicator("width")}
                          </th>
                          <th
                            width="10%"
                            className="text-start"
                            role="button"
                            onClick={() => this.onSort("remarks")}
                          >
                            Remarks {this.sortIndicator("remarks")}
                          </th>
                          <th
                            width="4%"
                            role="button"
                            onClick={() => this.onSort("internalId")}
                          >
                            {this.context.store.businessTypeId ==
                            this.context.BusinessTypes.STEELTRADERS
                              ? "SCNo"
                              : "Int.ID"}{" "}
                            {this.sortIndicator("internalId")}
                          </th>
                          <th
                            width="5%"
                            role="button"
                            onClick={() => this.onSort("batchId")}
                          >
                            {this.context.store.businessTypeId ==
                            this.context.BusinessTypes.STEELTRADERS
                              ? "CoilId"
                              : "BatchId"}{" "}
                            {this.sortIndicator("batchId")}
                          </th>
                          <th
                            width="5%"
                            role="button"
                            onClick={() => this.onSort("hsnCode")}
                          >
                            HSN {this.sortIndicator("hsnCode")}
                          </th>
                          <th
                            width="5%"
                            role="button"
                            onClick={() => this.onSort("qualityId")}
                          >
                            {this.context.store.businessTypeId ==
                            this.context.BusinessTypes.STEELTRADERS
                              ? "TR-NO"
                              : "Quality No"}{" "}
                            {this.sortIndicator("qualityId")}
                          </th>

                          <th width="5%" className="text-center">
                            Category
                          </th>
                          <th width="5%" className="text-center">
                            WH
                          </th>
                          {this.context.store.businessTypeId ==
                          this.context.BusinessTypes.STEELTRADERS ? (
                            <th
                              width="4%"
                              className="text-center"
                              role="button"
                              onClick={() => this.onSort("pieceCount")}
                            >
                              Pieces {this.sortIndicator("pieceCount")}
                            </th>
                          ) : null}
                          {this.context.store.businessTypeId ==
                          this.context.BusinessTypes.STEELTRADERS ? (
                            <th
                              width="6%"
                              className="text-center"
                              role="button"
                              onClick={() => this.onSort("localWt")}
                            >
                              LocalWt {this.sortIndicator("localWt")}
                            </th>
                          ) : null}
                          <th
                            width="6%"
                            className="text-center"
                            role="button"
                            onClick={() => this.onSort("instock")}
                          >
                            InStk Qty/Wt {this.sortIndicator("instock")}
                          </th>
                          <th width="6%" className="text-center">
                            Unit
                          </th>
                          <th
                            width="6%"
                            className="text-center"
                            role="button"
                            onClick={() => this.onSort("purchasePrice")}
                          >
                            P.Price / Unit {this.sortIndicator("purchasePrice")}
                          </th>
                          <th
                            width="5%"
                            className="text-center"
                            role="button"
                            onClick={() => this.onSort("value")}
                          >
                            Value
                          </th>
                          <th width="10%" className="text-end">
                            Options
                          </th>
                        </tr>
                      </>
                    ) : (
                      <></>
                    )}
                  </thead>
                  <tbody className="text-primary border">
                    {this.pagedItems().map((rawmaterialsinventory, i) => (
                      <EditRawMaterialsInventoryInline
                        key={
                          rawmaterialsinventory.id ||
                          `${rawmaterialsinventory.internalId}-${i}`
                        }
                        index={(this.state.page - 1) * this.state.pageSize + i}
                        rawmaterialsinventory={rawmaterialsinventory}
                        OnDeleted={this.OnDeleted}
                        rawmaterialcategory={this.state.rawmaterialcategory}
                        rawmaterialwarehouse={this.state.rawmaterialwarehouse}
                        lowStockThreshold={5}
                      />
                    ))}
                  </tbody>
                  <tfoot>
                    {(() => {
                      const ctx = this.context; // ✅ capture context here
                      const store = ctx.store;

                      // Total number of columns currently rendered
                      const totalCols = (() => {
                        let base = 14;
                        if (
                          store.businessTypeId ===
                          ctx.BusinessTypes.STEELTRADERS
                        ) {
                          base += 3; // Steel adds Thickness, Pieces, LocalWt
                        }
                        return base;
                      })();

                      const trailingCells =
                        store.businessTypeId === ctx.BusinessTypes.STEELTRADERS
                          ? 6
                          : 4;

                      const colSpanStart = totalCols - trailingCells;

                      return (
                        <tr className="text-center fw-bold bg-light">
                          <td
                            colSpan={colSpanStart}
                            className="text-end pe-2"
                          ></td>
                          {store.businessTypeId ===
                          ctx.BusinessTypes.STEELTRADERS ? (
                            <>
                              <td className="text-center">
                                {Number(t.pieceCount)?.toFixed(0)}
                              </td>
                              <td className="text-center">
                                {Number(t.localWt)?.toFixed(2)}
                              </td>
                              <td className="text-center">
                                {Number(t.instock)?.toFixed(2)}
                              </td>
                            </>
                          ) : null}
                          <td className="text-center"></td>
                          <td /> {/* Unit placeholder */}
                          <td className="text-center">{fmtMoney(t.value)}</td>
                          <td /> {/* Options placeholder */}
                        </tr>
                      );
                    })()}
                  </tfoot>
                </Table>
              </div>

              <div className="px-3 pb-4 d-flex justify-content-between align-items-center">
                <div>
                  <Form.Select
                    size="sm"
                    style={{ width: 120 }}
                    value={this.state.pageSize}
                    onChange={(e) =>
                      this.setState({
                        pageSize: Number(e.target.value),
                        page: 1,
                      })
                    }
                  >
                    {[10, 25, 50, 100].map((s) => (
                      <option key={s} value={s}>
                        {s} / page
                      </option>
                    ))}
                  </Form.Select>
                </div>
                <Pagination className="mb-0">
                  <Pagination.First
                    disabled={this.state.page === 1}
                    onClick={() => this.setPage(1)}
                  />
                  <Pagination.Prev
                    disabled={this.state.page === 1}
                    onClick={() => this.setPage(this.state.page - 1)}
                  />
                  <Pagination.Item active>{this.state.page}</Pagination.Item>
                  <Pagination.Next
                    disabled={this.state.page === totalPages}
                    onClick={() => this.setPage(this.state.page + 1)}
                  />
                  <Pagination.Last
                    disabled={this.state.page === totalPages}
                    onClick={() => this.setPage(totalPages)}
                  />
                </Pagination>
              </div>
            </div>
          </Row>
          {/* ✅ Analytics Modal */}
          <Modal
            show={this.state.showAnalytics}
            onHide={() => this.setState({ showAnalytics: false })}
            size="xl"
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>📊 RawMaterials Inventory Dashboard</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <RmInventoryDashboard />
            </Modal.Body>
          </Modal>
        </div>
      </>
    );
  }
}

export class EditRawMaterialsInventoryInline extends Component {
  static contextType = CartContext;
  constructor(props) {
    super(props);
    this.state = {
      rawmaterialsinventory: { pieceCount: 0, instock: 0 },
      msgboxOpen: false,
      msgboxTitle: "",
      msgboxMessage: "",
      loadSpinnerOpen: false,
      editModalShow: false,
      errors: {},
    };
  }

  ShowMessagBox = (title, message) =>
    this.setState({
      msgboxOpen: true,
      msgboxTitle: title,
      msgboxMessage: message,
    });
  CloseMessagBoxCallback = () => this.setState({ msgboxOpen: false });
  ShowLoadSpinner = () => this.setState({ loadSpinnerOpen: true });
  HideLoadSpinner = () => this.setState({ loadSpinnerOpen: false });

  // --- validation helpers ---
  isNumberField = (name) =>
    [
      "pieceCount",
      "localWt",
      "instock",
      "purchasePrice",
      "price",
      "weight",
    ].includes(name);
  validateField = (name, value) => {
    if (!this.isNumberField(name)) return null;
    if (value === "" || value === null || value === undefined)
      return "Required";
    if (isNaN(Number(value))) return "Must be a number";
    if (Number(value) < 0) return "Cannot be negative";
    return null;
  };

  OnDelete = () => {
    if (!this.state.rawmaterialsinventory.id) {
      this.ShowMessagBox(
        "Information",
        "This is invalid row which can't be deleted. Press Refresh!"
      );
      return;
    }

    this.ShowLoadSpinner();
    const requestOptions = {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
    };

    fetch(
      `${process.env.REACT_APP_API}Rawmaterials/DeleteRawMaterialsInventory/${this.state.rawmaterialsinventory.id}`,
      requestOptions
    )
      .then((res) => {
        if (res.status !== 200) {
          this.HideLoadSpinner();
          Swal.fire({
            title: "Information!",
            text: "Not deleted. This RawMaterialsInventory may be associated with few Products. First disassociate this rawmaterialsinventory from the Products and then try!",
            timer: 3000,
          });
          return;
        }
        this.HideLoadSpinner();
        this.ShowMessagBox(
          "Status",
          "Deleted Successfully. Click Refresh Button to see the changes!"
        );
        this.props.OnDeleted?.();
      })
      .catch((error) => {
        this.HideLoadSpinner();
        alert("OnDelete Failed " + error);
      });
  };

  handleInputChange = (e) => {
    const { name, value } = e.target;
    const r = { ...this.state.rawmaterialsinventory };
    const newVal = this.isNumberField(name)
      ? value === ""
        ? ""
        : value
      : value; // keep raw, validate separately

    r[name] = newVal;

    const errMsg = this.validateField(name, newVal);
    this.setState((s) => ({
      rawmaterialsinventory: r,
      errors: { ...s.errors, [name]: errMsg },
    }));
  };

  componentDidMount = () => {
    const r = this.props.rawmaterialsinventory;
    const errors = {};
    ["pieceCount", "localWt", "instock", "purchasePrice"].forEach((f) => {
      const msg = this.validateField(f, r?.[f]);
      if (msg) errors[f] = msg;
    });
    this.setState({ rawmaterialsinventory: r, errors });
  };
  OnEditButtonClick = () => this.setState({ editModalShow: true });
  editModalClose = () => this.setState({ editModalShow: false });

  OnSave = () => {
    // block save if any validation errors
    const fieldsToCheck = ["pieceCount", "localWt", "instock", "purchasePrice"];
    const errors = { ...this.state.errors };
    let hasErr = false;
    fieldsToCheck.forEach((f) => {
      const msg = this.validateField(f, this.state.rawmaterialsinventory?.[f]);
      if (msg) {
        errors[f] = msg;
        hasErr = true;
      }
    });
    if (hasErr) {
      this.setState({ errors });
      Swal.fire({
        title: "Validation",
        text: "Please fix highlighted fields.",
        timer: 2000,
      });
      return;
    }

    this.ShowLoadSpinner();

    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...this.state.rawmaterialsinventory,
        // coerce numeric fields to numbers before send
        pieceCount: Number(this.state.rawmaterialsinventory.pieceCount || 0),
        localWt: Number(this.state.rawmaterialsinventory.localWt || 0),
        instock: Number(this.state.rawmaterialsinventory.instock || 0),
        purchasePrice: Number(
          this.state.rawmaterialsinventory.purchasePrice || 0
        ),
      }),
    };

    fetch(
      `${process.env.REACT_APP_API}Rawmaterials/AddOrUpdateRawMaterialsInventory/`,
      requestOptions
    )
      .then((res) => {
        if (res.status === 200) {
          this.HideLoadSpinner();
          return;
        }
        this.HideLoadSpinner();
        Swal.fire({
          title: "Warning",
          text: "This RawMaterialsInventory already exists. Try again!",
          timer: 3000,
        });
      })
      .catch((error) => {
        this.HideLoadSpinner();
        alert("Exception. Try after sometime ! " + error);
      });
  };

  // prevent non-numeric keystrokes for numeric inputs
  numericKeyFilter = (e) => {
    const allowed = [
      "Backspace",
      "Delete",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "Home",
      "End",
      ".",
    ];
    if (e.ctrlKey || e.metaKey) return; // allow copy/paste
    if (allowed.includes(e.key)) return;
    if (!/^[0-9]$/.test(e.key)) e.preventDefault();
  };

  render() {
    const r = this.state.rawmaterialsinventory || {};

    const locale =
      this.context.storeSettings === ""
        ? "en-IN"
        : this.context.storeSettings.defaultLocale;
    const currency =
      this.context.storeSettings === ""
        ? "INR"
        : this.context.storeSettings.defaultCurrency;

    const valueNumber = Number(r.purchasePrice || 0) * Number(r.instock || 0);
    const valueDisplay = (() => {
      try {
        return valueNumber.toLocaleString(locale, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
          style: "currency",
          currency,
        });
      } catch {
        return valueNumber.toLocaleString("en-IN", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
          style: "currency",
          currency: "INR",
        });
      }
    })();

    const lowStock =
      this.props.lowStockThreshold != null &&
      Number(r.instock || 0) <= Number(this.props.lowStockThreshold);

    const err = (f) => !!this.state.errors?.[f];
    const feedback = (f) =>
      err(f) ? (
        <Form.Control.Feedback type="invalid">
          {this.state.errors[f]}
        </Form.Control.Feedback>
      ) : null;

    return (
      <>
        {this.state.editModalShow ? (
          <RawMaterialsInventoryAddOrEditModal
            show={this.state.editModalShow}
            onHide={this.editModalClose}
            rawmaterialsinventory={r}
            Type="Edit"
            rawmaterialcategory={this.props.rawmaterialcategory}
            rawmaterialwarehouse={this.props.rawmaterialwarehouse}
          />
        ) : null}
        {this.state.loadSpinnerOpen ? <LoadSpinner open="true" /> : null}

        {/* --- DESKTOP ROW (unchanged) visible md and up --- */}
        <tr
          className={`d-none d-md-table-row text-center align-middle text-secondary bg-white mx-5 ${
            lowStock ? "table-warning" : ""
          }`}
          style={{ fontSize: "12px" }}
        >
          {this.state.msgboxOpen ? (
            <MessageBox
              open={this.state.msgboxOpen}
              title={this.state.msgboxTitle}
              message={this.state.msgboxMessage}
              callback={this.CloseMessagBoxCallback}
            />
          ) : null}
          <td>{(this.props.index || 0) + 1}</td>
          <td className="text-start ">
            <Form.Control
              type="text"
              name="name"
              value={r.name || ""}
              className="text-start fw-bold"
              autoComplete="off"
              onChange={this.handleInputChange}
            />
          </td>
          <td>
            <Form.Control
              type="text"
              name="thickness"
              value={r.thickness || ""}
              className="text-start"
              autoComplete="off"
              onChange={this.handleInputChange}
            />
          </td>
          <td>
            <Form.Control
              type="text"
              name="width"
              value={r.width || ""}
              className="text-start"
              autoComplete="off"
              onChange={this.handleInputChange}
            />
          </td>
          <td>
            <Form.Control
              type="text"
              name="remarks"
              value={r.remarks || ""}
              className="text-start"
              autoComplete="off"
              onChange={this.handleInputChange}
            />
          </td>
          <td className="text-start ">
            <Form.Control
              type="text"
              name="internalId"
              value={r.internalId || ""}
              className="text-start "
              autoComplete="off"
              onChange={this.handleInputChange}
            />
          </td>
          <td className="text-start ">
            <Form.Control
              type="text"
              name="batchId"
              value={r.batchId || ""}
              className="text-start "
              autoComplete="off"
              onChange={this.handleInputChange}
            />
          </td>
          <td>
            <Form.Control
              type="text"
              name="hsnCode"
              value={r.hsnCode || ""}
              className="text-start "
              autoComplete="off"
              onChange={this.handleInputChange}
            />
          </td>
          <td>
            <Form.Control
              type="text"
              name="qualityId"
              value={r.qualityId || ""}
              className="text-start "
              autoComplete="off"
              onChange={this.handleInputChange}
            />
          </td>

          <td className="text-center ">
            <Form.Select
              name="categoryId"
              value={r.categoryId || ""}
              className="text-start "
              autoComplete="off"
              onChange={this.handleInputChange}
            >
              <option value=""></option>
              {this.props.rawmaterialcategory.map((m) => (
                <option value={m.id} key={m.id}>
                  {m.name}
                </option>
              ))}
            </Form.Select>
          </td>
          <td>
            <Form.Select
              name="warehouseId"
              value={r.warehouseId || ""}
              className="text-start "
              autoComplete="off"
              onChange={this.handleInputChange}
            >
              <option value=""></option>
              {this.props.rawmaterialwarehouse.map((m) => (
                <option value={m.id} key={m.id}>
                  {m.name}
                </option>
              ))}
            </Form.Select>
          </td>

          {this.context.store.businessTypeId ==
          this.context.BusinessTypes.STEELTRADERS ? (
            <td className="text-start ">
              <Form.Control
                type="text"
                name="pieceCount"
                value={r.pieceCount}
                className="text-center "
                autoComplete="off"
                onKeyDown={this.numericKeyFilter}
                isInvalid={err("pieceCount")}
                onChange={this.handleInputChange}
              />
              {feedback("pieceCount")}
            </td>
          ) : null}

          {this.context.store.businessTypeId ==
          this.context.BusinessTypes.STEELTRADERS ? (
            <td className="text-start">
              <Form.Control
                type="text"
                name="localWt"
                value={r.localWt}
                className="text-center text-danger "
                autoComplete="off"
                onKeyDown={this.numericKeyFilter}
                isInvalid={err("localWt")}
                onChange={this.handleInputChange}
              />
              {feedback("localWt")}
            </td>
          ) : null}

          <td className="text-start">
            <Form.Control
              type="text"
              name="instock"
              value={r.instock}
              className="text-center text-danger "
              autoComplete="off"
              onKeyDown={this.numericKeyFilter}
              isInvalid={err("instock")}
              onChange={this.handleInputChange}
            />
            {feedback("instock")}
          </td>

          <td className="text-start ">
            <Form.Select
              name="unit"
              value={r.unit || ""}
              className="text-center text-primary"
              autoComplete="off"
              onChange={this.handleInputChange}
            >
              <option value=""></option>
              <option value="piece">piece</option>
              <option value="gram">gram</option>
              <option value="kg">kg</option>
              <option value="ton">ton</option>
              <option value="ml">ml</option>
              <option value="liter">liter</option>
              <option value="meter">meter</option>
            </Form.Select>
          </td>

          <td className="text-center ">
            <Form.Control
              type="text"
              name="purchasePrice"
              value={r.purchasePrice}
              className="text-center text-primary "
              autoComplete="off"
              onKeyDown={this.numericKeyFilter}
              isInvalid={err("purchasePrice")}
              onChange={this.handleInputChange}
            />
            {feedback("purchasePrice")}
          </td>

          <td className="text-success">{valueDisplay}</td>

          <td className="col-md-2 text-end align-middle ">
            <Stack direction="horizontal">
              <OverlayTrigger overlay={<Tooltip>Save</Tooltip>}>
                <Button
                  size="sm"
                  className=" rounded-circle"
                  variant="success"
                  onClick={this.OnSave}
                >
                  <BiSave size="16" />
                </Button>
              </OverlayTrigger>
              &nbsp;
              <OverlayTrigger overlay={<Tooltip>Edit!</Tooltip>}>
                <Button
                  className="my-1 rounded-circle"
                  size="sm"
                  variant="warning"
                  onClick={this.OnEditButtonClick}
                >
                  <BsFillPencilFill size="16" />
                </Button>
              </OverlayTrigger>
              <OverlayTrigger
                overlay={
                  <Tooltip>Warning! This will delete all dependents.</Tooltip>
                }
              >
                <Button
                  className="mx-1  rounded-circle"
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    Swal.fire({
                      title: "Warning!",
                      confirmButtonColor: "#23B14D",
                      cancelButtonColor: "#dbdbdb",
                      text: "Are you sure you want to Delete?",
                      showCancelButton: true,
                      confirmButtonText: "Yes",
                      cancelButtonText: "No",
                    }).then((result) => {
                      if (result.isConfirmed) this.OnDelete();
                    });
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-trash"
                    viewBox="0 0 16 16"
                  >
                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
                    <path
                      fillRule="evenodd"
                      d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"
                    />
                  </svg>
                </Button>
              </OverlayTrigger>
            </Stack>
          </td>
        </tr>

        {/* --- MOBILE ROW: balanced 3-column layout with reduced field width --- */}

        <tr
          className={`d-table-row d-md-none border border-dark text-start bg-white ${
            lowStock ? "table-warning" : ""
          }`}
          style={{ fontSize: "12px" }}
        >
          <td colSpan="99" style={{ padding: 8 }}>
            <div
              // grid container: 3 equal columns, small gaps
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "8px 12px",
                alignItems: "start",
              }}
              // keep click on whole cell to open edit (same as desktop). If you don't want row click, remove onClick.
              onClick={(e) => {
                this.OnEditButtonClick && this.OnEditButtonClick(e);
              }}
              role="button"
            >
              {/* Column 1 items (grid cell 1,1), but we place each field as its own cell to keep rows aligned */}
              <div style={{ gridColumn: "1 / 2" }}>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>
                  {(this.props.index || 0) + 1}. {r.name || ""}
                </div>

                <div className="mb-1">
                  <label className="fw-semibold" style={{ fontSize: 12 }}>
                    Remarks
                  </label>
                  <Form.Control
                    type="text"
                    name="remarks"
                    value={r.remarks || ""}
                    size="sm"
                    onChange={this.handleInputChange}
                    style={{ maxWidth: "100%" }}
                  />
                </div>

                <div className="mb-1">
                  <label className="fw-semibold" style={{ fontSize: 12 }}>
                    Int.ID
                  </label>
                  <Form.Control
                    type="text"
                    name="internalId"
                    value={r.internalId || ""}
                    size="sm"
                    onChange={this.handleInputChange}
                    style={{ maxWidth: "100%" }}
                  />
                </div>

                <div className="mb-1">
                  <label className="fw-semibold" style={{ fontSize: 12 }}>
                    BatchId
                  </label>
                  <Form.Control
                    type="text"
                    name="batchId"
                    value={r.batchId || ""}
                    size="sm"
                    onChange={this.handleInputChange}
                    style={{ maxWidth: "100%" }}
                  />
                </div>

                <div className="mb-1">
                  <label className="fw-semibold" style={{ fontSize: 12 }}>
                    HSN
                  </label>
                  <Form.Control
                    type="text"
                    name="hsnCode"
                    value={r.hsnCode || ""}
                    size="sm"
                    onChange={this.handleInputChange}
                    style={{ maxWidth: "100%" }}
                  />
                </div>
              </div>

              {/* Column 2 */}
              <div style={{ gridColumn: "2 / 3" }}>
                <div className="mb-1">
                  <label className="fw-semibold" style={{ fontSize: 12 }}>
                    Quality No
                  </label>
                  <Form.Control
                    type="text"
                    name="qualityId"
                    value={r.qualityId || ""}
                    size="sm"
                    onChange={this.handleInputChange}
                    style={{ maxWidth: "100%" }}
                  />
                </div>

                <div className="mb-1">
                  <label className="fw-semibold" style={{ fontSize: 12 }}>
                    Category
                  </label>
                  <Form.Select
                    name="categoryId"
                    value={r.categoryId || ""}
                    size="sm"
                    onChange={this.handleInputChange}
                    style={{ maxWidth: "100%" }}
                  >
                    <option value=""></option>
                    {this.props.rawmaterialcategory.map((m) => (
                      <option value={m.id} key={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </Form.Select>
                </div>

                <div className="mb-1">
                  <label className="fw-semibold" style={{ fontSize: 12 }}>
                    Warehouse
                  </label>
                  <Form.Select
                    name="warehouseId"
                    value={r.warehouseId || ""}
                    size="sm"
                    onChange={this.handleInputChange}
                    style={{ maxWidth: "100%" }}
                  >
                    <option value=""></option>
                    {this.props.rawmaterialwarehouse.map((m) => (
                      <option value={m.id} key={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </Form.Select>
                </div>

                {this.context.store.businessTypeId ===
                  this.context.BusinessTypes.STEELTRADERS && (
                  <>
                    <div className="mb-1">
                      <label className="fw-semibold" style={{ fontSize: 12 }}>
                        Pieces
                      </label>
                      <Form.Control
                        type="text"
                        name="pieceCount"
                        value={r.pieceCount}
                        size="sm"
                        onKeyDown={this.numericKeyFilter}
                        isInvalid={err("pieceCount")}
                        onChange={this.handleInputChange}
                        style={{ maxWidth: "100%" }}
                      />
                      {feedback("pieceCount")}
                    </div>

                    <div className="mb-1">
                      <label className="fw-semibold" style={{ fontSize: 12 }}>
                        LocalWt
                      </label>
                      <Form.Control
                        type="text"
                        name="localWt"
                        value={r.localWt}
                        size="sm"
                        onKeyDown={this.numericKeyFilter}
                        isInvalid={err("localWt")}
                        onChange={this.handleInputChange}
                        style={{ maxWidth: "100%" }}
                      />
                      {feedback("localWt")}
                    </div>
                  </>
                )}
              </div>

              {/* Column 3 */}
              <div style={{ gridColumn: "3 / 4" }}>
                <div className="mb-1">
                  <label className="fw-semibold" style={{ fontSize: 12 }}>
                    InStock
                  </label>
                  <Form.Control
                    type="text"
                    name="instock"
                    value={r.instock}
                    size="sm"
                    onKeyDown={this.numericKeyFilter}
                    isInvalid={err("instock")}
                    onChange={this.handleInputChange}
                    style={{ maxWidth: "100%" }}
                  />
                  {feedback("instock")}
                </div>

                <div className="mb-1">
                  <label className="fw-semibold" style={{ fontSize: 12 }}>
                    Unit
                  </label>
                  <Form.Select
                    name="unit"
                    value={r.unit || ""}
                    size="sm"
                    onChange={this.handleInputChange}
                    style={{ maxWidth: "100%" }}
                  >
                    <option value=""></option>
                    <option value="piece">piece</option>
                    <option value="gram">gram</option>
                    <option value="kg">kg</option>
                    <option value="ton">ton</option>
                    <option value="ml">ml</option>
                    <option value="liter">liter</option>
                    <option value="meter">meter</option>
                  </Form.Select>
                </div>

                <div className="mb-1">
                  <label className="fw-semibold" style={{ fontSize: 12 }}>
                    P.Price / Unit
                  </label>
                  <Form.Control
                    type="text"
                    name="purchasePrice"
                    value={r.purchasePrice}
                    size="sm"
                    onKeyDown={this.numericKeyFilter}
                    isInvalid={err("purchasePrice")}
                    onChange={this.handleInputChange}
                    style={{ maxWidth: "100%" }}
                  />
                  {feedback("purchasePrice")}
                </div>
                <div className="mb-2">
                  <label className="fw-semibold" style={{ fontSize: 12 }}>
                    Value
                  </label>
                  <div className="text-success" style={{ marginTop: 4 }}>
                    {valueDisplay}
                  </div>
                </div>

                {/* action buttons (stop propagation) */}
                <div
                  style={{
                    marginTop: 8,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                  }}
                >
                  <OverlayTrigger overlay={<Tooltip>Save</Tooltip>}>
                    <Button
                      size="sm"
                      className="rounded-circle p-1"
                      variant="success"
                      onClick={(e) => {
                        e.stopPropagation();
                        this.OnSave && this.OnSave();
                      }}
                    >
                      <BiSave size="14" />
                    </Button>
                  </OverlayTrigger>

                  <OverlayTrigger overlay={<Tooltip>Edit!</Tooltip>}>
                    <Button
                      size="sm"
                      className="rounded-circle p-1"
                      variant="warning"
                      onClick={(e) => {
                        e.stopPropagation();
                        this.OnEditButtonClick && this.OnEditButtonClick(e);
                      }}
                    >
                      <BsFillPencilFill size="14" />
                    </Button>
                  </OverlayTrigger>

                  <OverlayTrigger overlay={<Tooltip>Delete</Tooltip>}>
                    <Button
                      size="sm"
                      className="rounded-circle p-1"
                      variant="danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        Swal.fire({
                          title: "Warning!",
                          text: "Are you sure you want to Delete?",
                          showCancelButton: true,
                          confirmButtonText: "Yes",
                          cancelButtonText: "No",
                        }).then((result) => {
                          if (result.isConfirmed) this.OnDelete();
                        });
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        fill="currentColor"
                        className="bi bi-trash"
                        viewBox="0 0 16 16"
                      >
                        <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
                        <path
                          fillRule="evenodd"
                          d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"
                        />
                      </svg>
                    </Button>
                  </OverlayTrigger>
                </div>
              </div>
            </div>
          </td>
        </tr>
      </>
    );
  }
}

export class RawMaterialsInventoryAddOrEditModal extends Component {
  static contextType = CartContext;
  constructor(props) {
    super(props);

    this.state = {
      rawmaterialsinventory: {
        name: "",
        pieceCount: 0,
        instock: 0,
        hsnCode: "",
        batchId: "",
        qualityId: "",
      },
      msgboxOpen: false,
      msgboxTitle: "",
      msgboxMessage: "",
      loadSpinnerOpen: false,
    };
  }

  CloseMessagBoxCallback = () => {
    this.setState({ msgboxOpen: false });
    this.props.onHide?.();
  };
  ShowMessagBox = (title, message) =>
    this.setState({
      msgboxOpen: true,
      msgboxTitle: title,
      msgboxMessage: message,
    });
  ShowLoadSpinner = () => this.setState({ loadSpinnerOpen: true });
  HideLoadSpinner = () => this.setState({ loadSpinnerOpen: false });

  AddOrUpdateRawMaterialsInventoryInDB = () => {
    this.ShowLoadSpinner();

    let tmpRawMaterialsInventory = { ...this.state.rawmaterialsinventory };
    if (this.props.Type === "Edit") {
    } else tmpRawMaterialsInventory.storeId = this.context.storeId;

    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmpRawMaterialsInventory),
    };

    fetch(
      `${process.env.REACT_APP_API}Rawmaterials/AddOrUpdateRawMaterialsInventory/`,
      requestOptions
    )
      .then((res) => {
        if (res.status === 200) {
          this.HideLoadSpinner();
          this.props.onHide?.();
          return;
        }
        this.HideLoadSpinner();
        Swal.fire({
          title: "Warning",
          text: "This RawMaterialsInventory already exists. try again!",
          timer: 3000,
        });
      })
      .catch((error) => {
        this.HideLoadSpinner();
        alert(
          "Exception. Received an Exception. Try after sometime ! " + error
        );
      });
  };

  handleSubmit = (event) => {
    event.preventDefault();
    this.AddOrUpdateRawMaterialsInventoryInDB();
  };

  handleInputChange = (e) => {
    const { name, value } = e.target;
    const r = { ...this.state.rawmaterialsinventory };

    switch (name.trim()) {
      case "warehouseId":
      case "name":
      case "hsnCode":
      case "qualityId":
      case "batchId":
      case "internalId":
      case "thickness":
      case "width":
      case "length":
      case "pieceCount":
      case "weight":
      case "localWt":
      case "unit":
      case "categoryId":
      case "instock":
      case "remarks":
        r[name] = value;
        break;
      case "purchasePrice":
        r[name] = value === "" ? "" : Number(value);
        break;
      default:
        break;
    }
    this.setState({ rawmaterialsinventory: r });
  };

  componentDidMount() {
    if (this.props.Type === "Edit") {
      this.setState({
        rawmaterialsinventory: this.props.rawmaterialsinventory,
      });
    } else {
      this.setState((s, p) => ({
        rawmaterialsinventory: {
          ...s.rawmaterialsinventory,
          unit:
            this.context.storeSettings?.defaultUnit ??
            s.rawmaterialsinventory.unit,
        },
      }));
    }
    setTimeout(() => {
      const el = document.getElementById("warehouseId");
      if (el) el.focus();
    }, 0);
  }

  render() {
    const r = this.state.rawmaterialsinventory || {};

    return (
      <div className="container ">
        <Modal
          {...this.props}
          size="xl"
          aria-labelledby="contained-modal-title-vcenter"
          centered
          backdrop="static"
          keyboard={false}
          dialogClassName="RawMaterialsInventoryModal"
        >
          {this.state.msgboxOpen ? (
            <MessageBox
              open={this.state.msgboxOpen}
              title={this.state.msgboxTitle}
              message={this.state.msgboxMessage}
              callback={this.CloseMessagBoxCallback}
            />
          ) : null}
          {this.state.loadSpinnerOpen ? <LoadSpinner open="true" /> : null}
          <Form onSubmit={this.handleSubmit}>
            <Modal.Header closeButton className="bg-warning">
              <Modal.Title id="contained-modal-title-vcenter">
                {this.props.Type === "Edit"
                  ? "Edit RawMaterial Product"
                  : "Add RawMaterial Product"}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="show-grid ">
              <Row
                className="m-2 p-2 bg-light border"
                style={{ fontSize: "12pt" }}
              >
                <Col md={12}>
                  <FloatingLabel label="Warehouse *" className="mb-2">
                    <Form.Select
                      id="warehouseId"
                      name="warehouseId"
                      size="lg"
                      value={r.warehouseId || ""}
                      className="text-start "
                      autoComplete="off"
                      onChange={this.handleInputChange}
                      required
                    >
                      <option value=""></option>
                      {this.props.rawmaterialwarehouse.map((m) => (
                        <option value={m.id} key={m.id}>
                          {m.name}
                        </option>
                      ))}
                    </Form.Select>
                  </FloatingLabel>

                  <Row className="mb-3">
                    <Col md={6}>
                      <FloatingLabel label="RawMaterial Name *">
                        <Form.Control
                          type="text"
                          size="lg"
                          id="name"
                          name="name"
                          maxLength={99}
                          value={r.name || ""}
                          required
                          autoComplete="off"
                          onChange={this.handleInputChange}
                        />
                      </FloatingLabel>
                    </Col>
                    {this.context.store.businessTypeId ==
                    this.context.BusinessTypes.STEELTRADERS ? (
                      <>
                        <Col md={2}>
                          <FloatingLabel label="Thickness *">
                            <Form.Control
                              type="number"
                              inputMode="decimal"
                              name="thickness"
                              className="text-center"
                              required
                              style={{ fontSize: "16px" }}
                              value={r.thickness ?? ""}
                              autoComplete="off"
                              onChange={this.handleInputChange}
                            />
                          </FloatingLabel>
                        </Col>
                        <Col md={2}>
                          <FloatingLabel label="Width *" className="mb-3">
                            <Form.Control
                              type="number"
                              inputMode="decimal"
                              name="width"
                              className="text-center"
                              required
                              style={{ fontSize: "16px" }}
                              value={r.width ?? ""}
                              autoComplete="off"
                              onChange={this.handleInputChange}
                            />
                          </FloatingLabel>
                        </Col>
                      </>
                    ) : null}
                    <Col md={2}>
                      <FloatingLabel label="Unit *">
                        <Form.Select
                          name="unit"
                          value={r.unit || ""}
                          className="text-start "
                          autoComplete="off"
                          onChange={this.handleInputChange}
                          required
                        >
                          <option value="piece">piece</option>
                          <option value="gram">gram</option>
                          <option value="kg">kg</option>
                          <option value="ton">ton</option>
                          <option value="ml">ml</option>
                          <option value="liter">liter</option>
                          <option value="meter">meter</option>
                        </Form.Select>
                      </FloatingLabel>
                    </Col>
                  </Row>
                  <FloatingLabel label="Remarks" className="mb-3">
                    <Form.Control
                      type="text"
                      size="lg"
                      name="remarks"
                      maxLength={254}
                      value={r.remarks || ""}
                      placeholder=""
                      autoComplete="off"
                      onChange={this.handleInputChange}
                    />
                  </FloatingLabel>
                  <Row>
                    <Col>
                      <FloatingLabel
                        label={
                          this.context.store.businessTypeId ==
                          this.context.BusinessTypes.STEELTRADERS
                            ? "SSD CoilNo"
                            : "Internal Id"
                        }
                        className="mb-3"
                      >
                        <Form.Control
                          type="text"
                          size="lg"
                          id="internalId"
                          name="internalId"
                          maxLength={49}
                          value={r.internalId || ""}
                          autoComplete="off"
                          onChange={this.handleInputChange}
                        />
                      </FloatingLabel>
                    </Col>
                    <Col>
                      <FloatingLabel
                        label={
                          this.context.store.businessTypeId ==
                          this.context.BusinessTypes.STEELTRADERS
                            ? "Coil Id"
                            : "Batch Id"
                        }
                        className="mb-3"
                      >
                        <Form.Control
                          type="text"
                          size="lg"
                          id="batchId"
                          name="batchId"
                          maxLength={99}
                          value={r.batchId || ""}
                          autoComplete="off"
                          onChange={this.handleInputChange}
                        />
                      </FloatingLabel>
                    </Col>
                    <Col>
                      <FloatingLabel
                        label={
                          this.context.store.businessTypeId ==
                          this.context.BusinessTypes.STEELTRADERS
                            ? "Test Report No"
                            : "Quality Id"
                        }
                        className="mb-3"
                      >
                        <Form.Control
                          type="text"
                          size="lg"
                          id="qualityId"
                          name="qualityId"
                          maxLength={99}
                          value={r.qualityId || ""}
                          autoComplete="off"
                          onChange={this.handleInputChange}
                        />
                      </FloatingLabel>
                    </Col>
                    <Col>
                      <FloatingLabel label="HSNCode" className="mb-3">
                        <Form.Control
                          type="text"
                          size="lg"
                          id="hsnCode"
                          name="hsnCode"
                          maxLength={99}
                          value={r.hsnCode || ""}
                          autoComplete="off"
                          onChange={this.handleInputChange}
                        />
                      </FloatingLabel>
                    </Col>
                  </Row>
                  {this.context.store.businessTypeId ==
                  this.context.BusinessTypes.STEELTRADERS ? (
                    <>
                      <FloatingLabel label="Pieces*" className="mb-3">
                        <Form.Control
                          type="number"
                          inputMode="numeric"
                          id="pieceCount"
                          className="text-start"
                          name="pieceCount"
                          value={r.pieceCount ?? 0}
                          required
                          autoComplete="off"
                          onChange={this.handleInputChange}
                        />
                      </FloatingLabel>
                    </>
                  ) : (
                    <></>
                  )}

                  <FloatingLabel
                    label={
                      this.context.store.businessTypeId ==
                      this.context.BusinessTypes.STEELTRADERS
                        ? "Purchased " + r.unit + " *"
                        : "InStock in" + r.unit + " *"
                    }
                    className="mb-3"
                  >
                    <Form.Control
                      type="number"
                      inputMode="decimal"
                      name="instock"
                      min={0}
                      value={r.instock ?? ""}
                      required
                      autoComplete="off"
                      onChange={this.handleInputChange}
                    />
                  </FloatingLabel>

                  {this.context.store.businessTypeId ==
                  this.context.BusinessTypes.STEELTRADERS ? (
                    <>
                      <FloatingLabel
                        label={"LocalWt in " + r.unit + " *"}
                        className="mb-3"
                      >
                        <Form.Control
                          type="text"
                          inputMode="decimal"
                          name="localWt"
                          min={0}
                          value={r.localWt ?? ""}
                          required
                          autoComplete="off"
                          onChange={this.handleInputChange}
                        />
                      </FloatingLabel>
                    </>
                  ) : (
                    <></>
                  )}

                  <FloatingLabel
                    label={"Purchase Price /" + r.unit + " *"}
                    className="mb-3"
                  >
                    <Form.Control
                      type="number"
                      inputMode="decimal"
                      name="purchasePrice"
                      min={0}
                      value={r.purchasePrice ?? ""}
                      required
                      autoComplete="off"
                      onChange={this.handleInputChange}
                    />
                  </FloatingLabel>

                  <FloatingLabel label="Category *">
                    <Form.Select
                      name="categoryId"
                      value={r.categoryId || ""}
                      className="text-start "
                      autoComplete="off"
                      onChange={this.handleInputChange}
                      required
                    >
                      <option value=""></option>
                      {this.props.rawmaterialcategory &&
                        this.props.rawmaterialcategory
                          .slice()
                          .sort((a, b) =>
                            a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1
                          )
                          .map((rc) => (
                            <option value={rc.id} key={rc.id}>
                              {rc.name}
                            </option>
                          ))}
                    </Form.Select>
                  </FloatingLabel>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer className="bg-light">
              <Form.Group>
                <Button variant="primary" size="lg" type="submit">
                  Save
                </Button>
              </Form.Group>
              <Button variant="danger" size="lg" onClick={this.props.onHide}>
                Close
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    );
  }
}
